import { useRef } from 'react';
import { Link } from 'react-router';
import CheckIcon from '@/assets/icons/check.svg?react';
import { AgentLogo, CLAUDE_STATUS, CODEX_STATUS } from '@/features/connections';
import { LINKS } from '@/shared/config/links';
import { ROUTES } from '@/shared/config/routes';
import { useEntranceMotion } from '@/shared/hooks/use-entrance-motion';
import { formatWeekdayDate } from '@/shared/lib/format';
import { summarizeUsage } from '@/shared/lib/pod-usage';
import type { WorkspaceData } from '@/shared/types/workspace';
import { Banner } from '@/shared/ui/banner';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Meter } from '@/shared/ui/meter';
import { Pill } from '@/shared/ui/pill';
import { claudeNote, codexNote } from '../model/connection-notes';
import { selectOverviewBanner } from '../model/overview-banner';
import { planCard, thirdCard, usageLine } from '../model/overview-cards';
import { ConnectionRow } from './connection-row';

type OverviewViewProps = { workspace: WorkspaceData };

/** Plan, Pod-hours and connections at a glance (design: Main). */
export function OverviewView({ workspace }: OverviewViewProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  useEntranceMotion(rootRef, 'overview');
  const usage = summarizeUsage(workspace.usage);
  const banner = selectOverviewBanner(workspace);
  const plan = planCard(workspace);
  const third = thirdCard(workspace);
  const { claudeCode, codex } = workspace.connections;

  return (
    <div ref={rootRef} className="contents">
      <header data-enter="" className="flex flex-col gap-1.5">
        <h1 className="font-semibold text-title">Overview</h1>
        <p className="text-muted">{formatWeekdayDate(new Date())}</p>
      </header>
      {banner ? (
        <div data-enter="" role={banner.role}>
          <Banner
            tone={banner.tone}
            title={banner.title}
            action={
              <Button asChild variant={banner.isPrimary ? 'primary' : 'default'}>
                <Link to={banner.href}>{banner.action}</Link>
              </Button>
            }
          >
            {banner.text}
          </Banner>
        </div>
      ) : null}
      <section
        data-enter=""
        aria-label="This period"
        className="grid grid-cols-[repeat(auto-fit,minmax(min(260px,100%),1fr))] gap-3"
      >
        <Card className="flex flex-col gap-2.5 px-5 py-4.5">
          <span className="text-muted">{usage.label}</span>
          <span className="font-semibold text-figure tabular-nums">
            {usage.used}{' '}
            <small className="font-medium text-muted text-section tracking-normal">
              of {usage.limit}
            </small>
          </span>
          <Meter ratio={usage.ratio} tone={usage.meterTone} label={usage.label} />
          <span className="text-meta text-muted">{usageLine(workspace)}</span>
        </Card>
        <Card className="flex flex-col gap-2.5 px-5 py-4.5">
          <span className="text-muted">Plan</span>
          <span className="font-semibold text-figure">{plan.name}</span>
          <span className="flex flex-wrap items-center gap-2">
            <Pill tone={plan.tone}>{plan.status}</Pill>
            <Link to={plan.href} className="font-medium text-accent-text text-meta">
              {plan.link}
            </Link>
          </span>
        </Card>
        <Card className="flex flex-col gap-2.5 px-5 py-4.5">
          <span className="text-muted">{third.label}</span>
          <span className="font-semibold text-figure tabular-nums">{third.value}</span>
          <span className="text-meta text-muted">{third.note}</span>
        </Card>
      </section>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(420px,100%),1fr))] items-start gap-3">
        <Card asChild>
          <section data-enter="" aria-labelledby="con-h" className="px-5 pt-4.5 pb-2">
            <div className="flex items-baseline justify-between gap-3">
              <h2 id="con-h" className="font-semibold text-section">
                Connections
              </h2>
              <Link to={ROUTES.CONNECTIONS} className="text-meta text-muted hover:text-ink">
                Manage
              </Link>
            </div>
            <div className="mt-1.5 flex flex-col">
              <ConnectionRow
                logo={<AgentLogo agent="claude-code" isCompact />}
                name="Claude Code"
                note={claudeNote(claudeCode)}
                status={CLAUDE_STATUS[claudeCode.status]}
              />
              <ConnectionRow
                logo={<AgentLogo agent="codex" isCompact />}
                name="Codex"
                note={codexNote(codex)}
                status={CODEX_STATUS[codex.status]}
              />
            </div>
          </section>
        </Card>
        <Card asChild>
          <section
            data-enter=""
            aria-labelledby="start-h"
            className="flex flex-col gap-3 px-5 py-4.5"
          >
            <h2 id="start-h" className="font-semibold text-section">
              Start a session
            </h2>
            <ul className="flex flex-col gap-2.5">
              <li className="grid grid-cols-[18px_1fr] items-start gap-2.5">
                <CheckIcon className="mt-0.5 size-4 text-accent-text" />
                <span>
                  In Claude Code, pick the{' '}
                  {claudeCode.environmentName ? (
                    <span className="font-mono text-ink text-mono">
                      {claudeCode.environmentName}
                    </span>
                  ) : (
                    'Macswarm'
                  )}{' '}
                  environment when you start a session.
                </span>
              </li>
              <li className="grid grid-cols-[18px_1fr] items-start gap-2.5">
                <CheckIcon className="mt-0.5 size-4 text-accent-text" />
                <span>In Codex, run the task in the environment that points at Macswarm.</span>
              </li>
              <li className="grid grid-cols-[18px_1fr] items-start gap-2.5">
                <CheckIcon className="mt-0.5 size-4 text-accent-text" />
                <span>A Pod boots in seconds and stops 10 minutes after the last session.</span>
              </li>
            </ul>
            <a href={LINKS.QUICKSTART} className="font-medium text-accent-text text-meta">
              Read the quickstart
            </a>
          </section>
        </Card>
      </div>
    </div>
  );
}
